import { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CURRENCY_SYMBOLS = { EUR: '€', GBP: '£', USD: '$' };
const ENTRY_FEE_MIN = 5;

/**
 * GoLiveModal — opens from the Open Golf demo's "Go Live and Start Fundraising"
 * banner button. Shows the 2nd-July go-live date (configurable via Super Admin
 * → /api/golf/config) and the two activation options whose card markup mirrors
 * the WC OnboardPage pricing picker exactly. Each card has a "Get started" CTA
 * that opens /onboard?sport=golf&pricing=<model>&… in a NEW TAB so the demo
 * stays behind.
 */
export default function GoLiveModal({ open, onClose, club, demoMode, sport = 'golf' }) {
  const isGaa = sport === 'gaa';
  const [goLive, setGoLive] = useState(isGaa ? '2026-07-26' : '2026-07-02');
  const [pricingModel, setPricingModel] = useState('per_entry');
  const [prices, setPrices] = useState({ EUR: 245, GBP: 195 });

  useEffect(() => {
    if (!open) return;
    if (isGaa) {
      axios.get(`${API}/gaa/config`).then(r => {
        if (r.data?.match_date) setGoLive(r.data.match_date);
      }).catch(() => {});
    } else {
      axios.get(`${API}/golf/config`).then(r => {
        if (r.data?.go_live_date) setGoLive(r.data.go_live_date);
      }).catch(() => {});
    }
    axios.get(`${API}/onboard/config`).then(r => {
      if (r.data?.prices) setPrices(r.data.prices);
    }).catch(() => {});
  }, [open, isGaa]);

  if (!open) return null;

  const currency = (club?.currency || 'EUR').toUpperCase();
  const currencySymbol = CURRENCY_SYMBOLS[currency] || '€';
  const currentFee = prices[currency] || prices.EUR || 245;
  const fee = parseFloat(club?.entry_fee ?? 10);
  const hasFee = Number.isFinite(fee) && fee >= ENTRY_FEE_MIN;
  const clubNetsMin = hasFee ? Math.floor(fee - 0.99) : null;

  // Pretty-format YYYY-MM-DD → "2nd July 2026"
  const formatDate = (iso) => {
    try {
      const d = new Date(`${iso}T00:00:00`);
      const day = d.getDate();
      const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
      const suffix = (n) => {
        if (n >= 11 && n <= 13) return 'th';
        const last = n % 10;
        return last === 1 ? 'st' : last === 2 ? 'nd' : last === 3 ? 'rd' : 'th';
      };
      return `${day}${suffix(day)} ${months[d.getMonth()]} ${d.getFullYear()}`;
    } catch (_) { return iso; }
  };

  const buildOnboardUrl = (model) => {
    const params = new URLSearchParams({
      sport: isGaa ? 'gaa' : 'golf',
      pricing: model,
      currency,
      entry_fee: String(fee || 10),
    });
    if (club?.name) params.set('club_name', club.name);
    if (club?.primary_color) params.set('primary_colour', club.primary_color);
    if (club?.secondary_color) params.set('secondary_colour', club.secondary_color);
    if (club?.logo_url) params.set('logo_url', club.logo_url);
    if (club?.admin_email) params.set('email', club.admin_email);
    return `/onboard?${params.toString()}`;
  };

  const handleGetStarted = (model) => {
    window.open(buildOnboardUrl(model), '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center px-4 py-8 bg-black/60" data-testid="golf-go-live-modal" onClick={onClose}>
      <div
        className="relative bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        {/* Close */}
        <button
          type="button"
          onClick={onClose}
          className="absolute top-4 right-4 w-9 h-9 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-500 hover:text-gray-700 transition-colors"
          aria-label="Close"
          data-testid="golf-go-live-modal-close"
        >
          <X size={18} />
        </button>

        <div className="p-6 sm:p-10">
          {/* Header */}
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#E63946] mb-2">{isGaa ? 'All-Ireland Final 2026 Predictor' : 'Open Golf 2026 Predictor'}</p>
          <h2 className="text-2xl sm:text-3xl font-black text-[#1D3557] leading-tight mb-3" style={{ fontFamily: 'Playfair Display, serif' }} data-testid="golf-go-live-headline">
            Activate {club?.name || 'your club'}
          </h2>
          {isGaa ? (
            <p className="text-gray-700 text-sm sm:text-base leading-relaxed mb-2">
              Your All-Ireland Final Predictor goes live <strong className="text-[#1D3557]">the moment your Stripe setup completes</strong>. Entries close at throw-in — Croke Park, <strong className="text-[#1D3557]">Sunday 26 July</strong>.
            </p>
          ) : (
            <p className="text-gray-700 text-sm sm:text-base leading-relaxed mb-2">
              The Open Golf Predictor goes live on <strong className="text-[#1D3557]" data-testid="golf-go-live-date">{formatDate(goLive)}</strong> — two weeks before The Open tees off at Royal Birkdale.
            </p>
          )}
          <p className="text-gray-700 text-sm sm:text-base leading-relaxed mb-6">
            {isGaa
              ? 'Get set up with Stripe now and start collecting entries today — every day before the final counts.'
              : 'Get set up with Stripe now so you\u2019re ready to go the moment we launch. Your predictor stays private until then.'}
          </p>

          {/* Pricing picker — same card markup as WC OnboardPage */}
          <section data-testid="golf-pricing-picker">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#1D3557]/60 mb-3">Choose your pricing</p>

            <div className="grid sm:grid-cols-2 gap-3 mb-5">
              {/* Per-entry card */}
              <button
                type="button"
                onClick={() => setPricingModel('per_entry')}
                data-testid="golf-pricing-per-entry"
                className={`text-left p-4 rounded-lg border-2 transition-all ${
                  pricingModel === 'per_entry'
                    ? 'border-[#E63946] bg-[#FFF5F5] shadow-sm'
                    : 'border-gray-200 bg-white hover:border-gray-300'
                }`}
              >
                <div className="flex items-start justify-between mb-2">
                  <span className="text-base font-black uppercase tracking-wider text-[#E63946]">{currencySymbol}0 upfront</span>
                  <span className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${pricingModel === 'per_entry' ? 'border-[#E63946] bg-[#E63946]' : 'border-gray-300'}`}>
                    {pricingModel === 'per_entry' && <span className="block w-2 h-2 bg-white rounded-full" />}
                  </span>
                </div>
                <p className="font-bold text-[#1D3557] text-lg leading-tight mb-1">Pay per entry</p>
                {hasFee && (
                  <p className="text-sm font-bold text-[#E63946] leading-snug mb-1">
                    Over {currencySymbol}{clubNetsMin} from each {currencySymbol}{fee} entry goes straight to your club.
                  </p>
                )}
                <p className="text-xs text-gray-600 leading-relaxed">
                  <strong>{currencySymbol === '€' ? '99c' : '99p'} per entry</strong> — includes Stripe payment fees ({currencySymbol === '€' ? '49c' : '42p'}) and TargetFunder fees ({currencySymbol === '€' ? '50c' : '57p'}).
                </p>
              </button>

              {/* Setup-fee card */}
              <button
                type="button"
                onClick={() => setPricingModel('setup_fee')}
                data-testid="golf-pricing-setup-fee"
                className={`text-left p-4 rounded-lg border-2 transition-all ${
                  pricingModel === 'setup_fee'
                    ? 'border-[#1D3557] bg-[#F0F4F8] shadow-sm'
                    : 'border-gray-200 bg-white hover:border-gray-300'
                }`}
              >
                <div className="flex items-start justify-between mb-2">
                  <span className="text-xs font-bold uppercase tracking-wider text-[#1D3557]">One-off fee</span>
                  <span className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${pricingModel === 'setup_fee' ? 'border-[#1D3557] bg-[#1D3557]' : 'border-gray-300'}`}>
                    {pricingModel === 'setup_fee' && <span className="block w-2 h-2 bg-white rounded-full" />}
                  </span>
                </div>
                <p className="font-bold text-[#1D3557] text-lg leading-tight mb-1">Pay {currencySymbol}{currentFee} once</p>
                <p className="text-xs text-gray-600 leading-relaxed">
                  One-time setup fee. After that, you keep <strong>100% of every entry</strong> — no per-entry platform fee, ever. Stripe payment processing fee still applies ({currencySymbol === '€' ? '49c' : '42p'} per entry).
                </p>
              </button>
            </div>
          </section>

          {/* CTA */}
          <button
            type="button"
            onClick={() => handleGetStarted(pricingModel)}
            className="w-full px-6 py-4 text-sm sm:text-base font-bold uppercase tracking-widest rounded-lg bg-[#E63946] hover:bg-[#d32f3c] text-white transition-colors shadow-md"
            data-testid="golf-go-live-cta"
          >
            Get started with Stripe →
          </button>
          <p className="text-xs text-gray-500 text-center mt-3">{isGaa ? 'Opens Stripe Connect in a new tab. You go live as soon as setup completes.' : `Opens Stripe Connect in a new tab. Predictor stays private until ${formatDate(goLive)}.`}</p>

          {demoMode && (
            <p className="text-[11px] text-gray-400 text-center mt-4 italic">This is a demo — Stripe setup is real. Your live predictor link is generated once activation completes.</p>
          )}
        </div>
      </div>
    </div>
  );
}
